import {
  CATALOG,
  GATES,
  LIBRARY_GROUPS,
  RZ_CHANNELS,
  RZ_COND,
  RZ_FEED_FOR,
  RZ_FEEDS,
  RZ_MODULE_STEPS,
  RZ_SIGNALS,
  RZ_STYLE,
  RZ_TEMPLATES,
  RZ_TRIG_HINT,
  TRIGGERS,
} from "./s09-catalog.data";

/** API list row (from api/workflows/get-all). */
export interface WorkflowDefinition {
  wf_def_id: number;
  wf_name: string;
  description?: string;
  status?: string;
  version?: number;
  trigger_type?: string;
  last_run_at?: string;
  instance_count?: number;
  failure_rate?: number;
  created_by?: string;
  created_time?: string;
  updated_by?: string;
  updated_time?: string;
  activated_time?: string;
}

export type S09StepKind = keyof typeof CATALOG | string;

export interface S09Attempt {
  day: string;
  tpl: string;
  tplText?: string;
  channel: string;
  hub: string;
  ask: string;
  /** Steps and conditions that run after this attempt, before the next one. */
  after: S09ModuleStep[];
}

/** A catalog step nested inside an Office Outreach Module. */
export interface S09ModuleStep {
  id: string;
  kind: string;
  values: Record<string, any>;
}

export interface S09OutreachMod {
  wait: number;
  every: number;
  escalate: boolean;
  optional: boolean;
  attempts: S09Attempt[];
  /** Ordered workflow steps that run with this module. */
  steps: S09ModuleStep[];
}

/** Canvas geometry for a free-position designer node. */
export interface S09NodeGeom {
  x: number;
  y: number;
  w: number;
  h?: number;
}

export interface S09Step {
  id: string;
  kind: S09StepKind;
  values: Record<string, any>;
  mod?: S09OutreachMod;
  /** Free-canvas position / size (persisted in DESIGN_JSON). */
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

export interface S09Exits {
  progress: string[];
  exhausted: string[];
  onExhausted: string;
  cancelTo: string;
  early: string[];
  progressRaw?: string;
  exhaustedRaw?: string;
  earlyRaw?: string;
}

export interface S09Meta {
  code: string;
  group: "patient" | "account" | "shared" | string;
  codes: string[];
  hub: string;
  module?: S09OutreachMod | null;
  progress: string[];
  exhausted: string[];
  onExhausted: string;
  cancelTo: string;
  early: string[];
  threshold?: string;
  feed?: string;
  priority?: string;
  /** Display name from Case Type API */
  caseType?: string;
  caseTypeKey?: number | null;
  caseTypeCode?: string;
  guardrails?: string[];
  openItems?: string[];
}

export interface CaseTypeOption {
  typeKey: number;
  typeCode: string;
  name: string;
  status?: string;
  slaLabel?: string;
}

/** One stage in a case type's Case Flow. */
export interface CaseStage {
  stage_id?: number;
  brand_key: number;
  case_type_key: number;
  stage_name: string;
  sort_order: number;
  is_active?: boolean;
  /** Stage names this stage is allowed to move to. Absent means a linear chain. */
  moves_to?: string[];
}

export interface EntrySignalOption {
  signal_id: number;
  signal_code: string;
  /** Description from patient status dim (or seed label) — used for tooltips */
  signal_label?: string;
  sort_order?: number;
}

/** Reason code from DU_ANA_PATIENT_STATUS_DIM for the entry-signal picker */
export interface ReasonCodeOption {
  reason_code: string;
  reason_desc?: string;
}

/** Hover text: "PH02.7 — Missing demographics" when desc differs from code. */
export function entrySignalTooltip(s: {
  signal_code?: string;
  signal_label?: string;
}): string {
  const code = String(s?.signal_code || "").trim();
  const label = String(s?.signal_label || "").trim();
  if (!code) return label;
  if (!label || label === code) return code;
  return `${code} — ${label}`;
}

export interface EntrySignalGroup {
  group_id: number;
  group_code: string;
  group_label: string;
  sort_order?: number;
  signals: EntrySignalOption[];
}

export interface TriggerConditionOption {
  condition_id: number;
  trigger_type: string;
  condition_code: string;
  condition_label: string;
  sort_order?: number;
}

/** Outreach / script template from DU_ANA_WF_TEMPLATE */
export interface WfTemplate {
  template_id?: number;
  brand_key?: number;
  template_code: string;
  template_name: string;
  kind: string;
  source_status: "doc" | "draft" | string;
  subject?: string;
  body?: string;
  sort_order?: number;
  is_active?: boolean;
  /** Alias used by designer dropdowns (same as template_code) */
  id?: string;
  /** Alias used by designer dropdowns (same as template_name) */
  name?: string;
}

/** Data-signal dictionary row from DU_ANA_WF_DATA_SIGNAL */
export interface WfDataSignal {
  data_signal_id?: number;
  brand_key?: number;
  trigger_label: string;
  signals_text: string;
  source_feed?: string;
  gap_note?: string;
  sort_order?: number;
}

/** Which edge of a step an incoming connector attaches to. Missing means the top. */
export type S09Side = "top" | "left" | "right";

/** One labeled output on a step. If / Else uses true and false. FRM Decision uses escalate and wait. */
export type S09Branch = "true" | "false" | "escalate" | "wait";

export function isS09Branch(value: unknown): value is S09Branch {
  return value === "true" || value === "false" || value === "escalate" || value === "wait";
}

/** Document stored in DESIGN_JSON. */
export interface S09Link {
  from: string;
  to: string;
  /** Set when a step has more than one output. */
  branch?: S09Branch;
  /** Elbow position, so the line can be routed around other steps. */
  bendX?: number;
  bendY?: number;
  /** Edge of `to` the arrow attaches to. Missing means the top. */
  toSide?: S09Side;
  /** Edge of `from` the line leaves. Missing means the bottom output. */
  fromSide?: "left" | "right";
}

export interface S09DesignDoc {
  s09: 1;
  meta: S09Meta;
  trigger: string;
  triggerValue?: string;
  triggerSource?: string;
  steps: S09Step[];
  /** Canvas edges. `from` is "trigger" or a step id. Missing means a straight chain. */
  links?: S09Link[];
  exits: S09Exits;
  /** Positions for non-step canvas chrome (trigger pill, exits block). */
  layout?: {
    trigger?: S09NodeGeom;
    exits?: S09NodeGeom;
  };
}

export interface S09CreateDraft {
  from: string;
  group: string;
  code: string;
  name: string;
  priority: string;
  caseType: string;
  caseTypeKey: number | null;
  caseTypeCode: string;
  trigger: string;
  codes: string[];
  threshold: string;
  feed: string;
  hub: string;
  guardrails: string[];
  openItems: string[];
  sigOther: string;
  /** When set, the new workflow copies this definition's canvas. */
  sourceWfDefId: number | null;
}

export const LIST_COLS = [
  "Workflow",
  "Trigger",
  "Status",
  "Version",
  "Owner",
  "Last run",
  "Instances",
  "Failure %",
  "Actions",
] as const;

export const RZ_TRIGGER_IDS = [
  "status",
  "absence",
  "threshold",
  "event",
  "schedule",
  "module",
  "manual",
] as const;

export function blankCreateDraft(): S09CreateDraft {
  return {
    from: "",
    group: "patient",
    code: "",
    name: "",
    priority: "Medium",
    caseType: "",
    caseTypeKey: null,
    caseTypeCode: "",
    trigger: "status",
    codes: [],
    threshold: "",
    feed: "",
    hub: "",
    guardrails: [],
    openItems: [],
    sigOther: "",
    sourceWfDefId: null,
  };
}

let _modStepSeq = 0;
export function nextModuleStepId(): string {
  _modStepSeq += 1;
  return `ms_${Date.now().toString(36)}_${_modStepSeq}`;
}

/** Empty catalog values. Multi-select fields start as []. */
export function blankStepValues(kind: string): Record<string, any> {
  const cat = catalogEntry(kind);
  const values: Record<string, any> = {};
  (cat?.fields || []).forEach((f: any) => {
    values[f.key] = f.briefTpl ? { "HCP office": "" } : f.multi ? [] : "";
  });
  if (kind === "frmDecision") {
    values["ask"] = "Can this case escalate, or should it wait?";
    values["unit"] = "Business days";
  }
  if (kind === "assignCase") values["team"] = "FRM leadership";
  if (kind === "wait") values["from"] = "Since the last step";
  return values;
}

export function defaultFrmActionStep(): S09ModuleStep {
  return {
    id: nextModuleStepId(),
    kind: "frmAction",
    values: blankStepValues("frmAction"),
  };
}

function asDayList(value: any): string[] {
  if (Array.isArray(value)) return value.map((d) => String(d)).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(/[,·]/)
      .map((d) => d.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeAttempt(attempt: Partial<S09Attempt> | null | undefined): S09Attempt {
  return {
    day: attempt?.day || "",
    tpl: attempt?.tpl || "",
    tplText: attempt?.tplText || "",
    channel: attempt?.channel || "",
    hub: attempt?.hub || "",
    ask: attempt?.ask || "",
    after: Array.isArray(attempt?.after) ? attempt.after.map((s) => normalizeModuleStep(s)) : [],
  };
}

function normalizeModuleStep(step: Partial<S09ModuleStep> | null | undefined): S09ModuleStep {
  const kind = String(step?.kind || "frmAction");
  const values = { ...blankStepValues(kind), ...(step?.values || {}) };
  if (kind === "frmAction") values["days"] = asDayList(values["days"]);
  return {
    id: step?.id || nextModuleStepId(),
    kind,
    values,
  };
}

/**
 * Fill a module that was saved before inner steps existed.
 * An explicit empty `steps` array is left empty.
 */
export function ensureModSteps(mod: S09OutreachMod | null | undefined): S09OutreachMod {
  const attempts = (mod?.attempts?.length ? mod.attempts : [{}]).map((a) => normalizeAttempt(a));
  const steps = Array.isArray(mod?.steps)
    ? mod.steps.map((s) => normalizeModuleStep(s))
    : [defaultFrmActionStep()];
  return {
    wait: mod?.wait ?? 0,
    every: mod?.every ?? 0,
    escalate: !!mod?.escalate,
    optional: !!mod?.optional,
    attempts,
    steps,
  };
}

/** Blank outreach module shell — starts with one FRM Action step. */
export function defaultMod(): S09OutreachMod {
  return {
    wait: 0,
    every: 0,
    escalate: false,
    optional: false,
    attempts: [
      { day: "", tpl: "", tplText: "", channel: "", hub: "", ask: "", after: [] },
    ],
    steps: [defaultFrmActionStep()],
  };
}

export function defaultExits(): S09Exits {
  return {
    progress: [],
    exhausted: [],
    onExhausted: "",
    cancelTo: "",
    early: [],
  };
}

export function catalogEntry(kind: string) {
  return (CATALOG as any)[kind] as
    | {
        group: string;
        label: string;
        sub: string;
        colour: string;
        path: string;
        fields?: any[];
        gated?: boolean;
        future?: boolean;
        stageStep?: boolean;
      }
    | undefined;
}

export function triggerMeta(id: string) {
  return (TRIGGERS as any[]).find((t: any) => t.id === id) || (TRIGGERS as any[])[0];
}

export function templateName(tplId: string): string {
  const t = (RZ_TEMPLATES as any[]).find(
    (x: any) => x.id === tplId || x.name === tplId
  );
  return t?.name || tplId || "Pick a script";
}

export function templateById(tplId: string): any | null {
  return (
    (RZ_TEMPLATES as any[]).find(
      (x: any) => x.id === tplId || x.name === tplId
    ) || null
  );
}

export function seedMetaFromStyle(styleKey: string): S09Meta | null {
  const st = (RZ_STYLE as any)[styleKey];
  if (!st) return null;
  return JSON.parse(JSON.stringify(st)) as S09Meta;
}

export function styleKeys(): string[] {
  return Object.keys(RZ_STYLE);
}

export function shortName(fullName: string, code: string): string {
  return fullName
    .replace(/^[^·]*·\s*/, "")
    .replace(new RegExp("^" + code + "\\s+"), "");
}

export function apiTriggerType(
  s09Trigger: string
): "schedule" | "event" | "manual" {
  if (s09Trigger === "schedule") return "schedule";
  if (s09Trigger === "manual") return "manual";
  return "event";
}

export function defaultFeedFor(_trigger: string): string {
  return "";
}

export function triggerHint(trigger: string): string {
  return (RZ_TRIG_HINT as any)[trigger] || "";
}

export {
  CATALOG,
  GATES,
  LIBRARY_GROUPS,
  TRIGGERS,
  RZ_STYLE,
  RZ_TEMPLATES,
  RZ_CHANNELS,
  RZ_COND,
  RZ_FEEDS,
  RZ_MODULE_STEPS,
  RZ_SIGNALS,
  RZ_TRIG_HINT,
  RZ_FEED_FOR,
};
